// People's names, for display and for search.
//
// Search compares a normalised key rather than the names as typed, because the same
// person arrives as "Wanjiru Kamau", "KAMAU Wanjiru", "Kamau, Wanjiru" and "Wanjirũ
// Kamau" at different desks. The key is:
//
//   1. decomposed (NFD) and stripped of combining accents, so ũ and u compare equal;
//   2. lower-cased;
//   3. every run of characters that is not a letter or a digit turned into one space;
//   4. split into words, sorted by code point, and joined with single spaces.
//
// Sorting the words makes name order irrelevant: given, middle and family names are
// written in different orders by different people, and a receptionist searching does not
// know which order the record used.
//
// The database computes the same key in `app_name_search_key()` for the generated
// `patients.name_search` column; an integration test checks the two agree. Change both
// together or search silently stops finding people.

/** A patient's name parts as stored. Only the given name is always present. */
export interface PersonName {
  readonly given: string;
  readonly middle?: string | null | undefined;
  readonly family?: string | null | undefined;
}

const COMBINING_MARKS = /[̀-ͯ]/g;
const NOT_LETTER_OR_DIGIT = /[^\p{L}\p{N}]+/gu;

/** Compares by Unicode code point, which is the order Postgres's "C" collation uses. */
function compareCodePoints(a: string, b: string): number {
  const left = Array.from(a);
  const right = Array.from(b);
  const length = Math.min(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (left[index]?.codePointAt(0) ?? 0) - (right[index]?.codePointAt(0) ?? 0);
    if (difference !== 0) return difference;
  }
  return left.length - right.length;
}

/** The search key for free text: a query, or one name part. */
export function nameSearchKey(text: string): string {
  return text
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(NOT_LETTER_OR_DIGIT, ' ')
    .split(' ')
    .filter((word) => word !== '')
    .sort(compareCodePoints)
    .join(' ');
}

/** The search key for a stored name; mirrors `app_name_search_key(given, middle, family)`. */
export function normaliseNameForSearch(
  given: string,
  middle?: string | null,
  family?: string | null,
): string {
  return nameSearchKey([given, middle ?? '', family ?? ''].join(' '));
}

/**
 * The name as a person reads it: given, middle and family, in that order, with blank parts
 * left out and runs of spaces collapsed. Case and spelling are kept exactly as recorded.
 */
export function displayName(name: PersonName): string {
  return [name.given, name.middle ?? '', name.family ?? '']
    .map((part) => part.trim().replace(/\s+/g, ' '))
    .filter((part) => part !== '')
    .join(' ');
}
